import {
  AiScribeContentOutputType,
  AiScribeJob,
  hasGeneratedScribeOutput,
  SiblingContentOutputType,
  SUB_REPORT_STATUS,
} from '../interfaces/aiScribeJob.interface';
import { GeneratedDocumentType } from '../interfaces/generated-document.interface';
import { ReportVariant } from '../interfaces/report.interface';

// GeneratedDocumentType for the PTP narrative report's DOCX.
export const PTP_REPORT_DOCUMENT_TYPE = 'PTP_REPORT' as const;

// Report outputType on the session.
export const PTP_REPORT_OUTPUT_TYPE = 'ptp_report' as const;

// Generation use case for the report's templates
export const PTP_REPORT_GENERATION_USE_CASE = 'ptp_report' as const;

// Each PTP template package belongs to one report type, named by its profile
// set key. Org copies of a package keep the key, so this map stays fixed.
export const PTP_REPORT_VARIANT_BY_PROFILE_SET_KEY: Readonly<
  Record<string, ReportVariant>
> = {
  ptp_comprehensive: 'comprehensive',
  ptp_follow_up: 'follow_up',
  ptp_supplemental: 'supplemental',
};

/**
 * Every AI Scribe form type in the PTP report family.
 *
 * A form type listed here routes its report tab through
 * PtpAiScribeReportComponent, which shows the generated DOCX when the job has
 * a content row of the matching outputType and otherwise falls back to the
 * legacy ai-scribe-report view. That fallback is what makes adding a form type
 * here safe before its Temporal flow is rolled out everywhere: an org still on
 * the legacy path never gets the content row, so it keeps the old view
 * indefinitely rather than showing a stuck "waiting for generation" state.
 */
export const PTP_FORM_TYPES = ['pr-1', 'pr-2', 'pr-4'] as const;

export type PtpFormType = (typeof PTP_FORM_TYPES)[number];

/**
 * Standalone-form AI Scribe job: generates ONE sibling form — Injection, RFA
 * or Work Status — ON ITS OWN, with no PR-1 / PR-2 report behind it. Its
 * scribe shows exactly two tabs — Transcription and that form — and never a
 * PR report tab.
 *
 * The form type IS the form's sibling variant, so every per-form value (label,
 * outputType, gate) comes from SIBLING_REPORT_CONFIG.
 *
 * Deliberately NOT in PTP_FORM_TYPES: that list routes the report tab through
 * the PR report view, which this job does not have. And deliberately not
 * spelled 'pr-…': several checks treat any 'pr-' form type as a PR report.
 */
export const STANDALONE_FORM_TYPES = [
  'injection',
  'rfa',
  'work-status',
] as const satisfies readonly SiblingReportVariant[];

/** The sibling form a standalone-form job generates, or null for any other form type. */
export function getStandaloneFormVariant(
  formType: string | null | undefined,
): SiblingReportVariant | null {
  const value = (formType ?? '').toLowerCase();
  return (STANDALONE_FORM_TYPES as readonly string[]).includes(value)
    ? (value as SiblingReportVariant)
    : null;
}

export function isStandaloneFormType(
  formType: string | null | undefined,
): boolean {
  return getStandaloneFormVariant(formType) !== null;
}

export function isPtpFormType(
  formType: string | null | undefined,
): formType is PtpFormType {
  return (PTP_FORM_TYPES as readonly string[]).includes(formType ?? '');
}

interface PtpFormTypeConfig {
  documentType: GeneratedDocumentType;
  outputType: AiScribeContentOutputType;
  reportLabel: string;
}

/**
 * Single source of truth for every PTP-family lookup, keyed by form type.
 * Adding a new PTP report kind means adding one entry here rather than one
 * per lookup map.
 */
export const PTP_FORM_TYPE_CONFIG: Record<PtpFormType, PtpFormTypeConfig> = {
  'pr-1': {
    documentType: 'PR1',
    outputType: 'PR1',
    reportLabel: 'PR-1 Report',
  },
  'pr-2': {
    documentType: 'PR2',
    outputType: 'PR2',
    reportLabel: 'PR-2 Report',
  },
  'pr-4': {
    documentType: 'PR4_REPORT',
    outputType: 'PR4',
    reportLabel: 'PR-4 Report',
  },
};

/**
 * True once a PR and child reports has been generated for this scribe.
 */
export function hasGeneratedPrReport(
  job:
    | Pick<
        AiScribeJob,
        | 'formType'
        | 'contentIdByOutputType'
        | 'AiScribeContents'
        | 'subReportStatuses'
      >
    | null
    | undefined,
): boolean {
  if (!job) return false;
  const ids = job.contentIdByOutputType ?? {};
  if (
    Object.values(PTP_FORM_TYPE_CONFIG).some(
      ({ outputType }) => !!ids[outputType],
    )
  ) {
    return true;
  }
  // Standalone-form job: its one report is its form — a report row, or a
  // gate that answered "not required" (final, but writes no row).
  // Queued / generating are covered by the callers' status checks, as for PR.
  const standaloneVariant = getStandaloneFormVariant(job.formType);
  if (standaloneVariant) {
    const { outputType } = SIBLING_REPORT_CONFIG[standaloneVariant];
    if (
      ids[outputType] ||
      job.subReportStatuses?.[outputType] === SUB_REPORT_STATUS.NOT_REQUIRED
    ) {
      return true;
    }
  }
  // PTP scribes only: a non-PR scribe's LEGACY row is its transcription
  // summary, which can be cleared (append) and must not read as a report.
  if (isPtpFormType(job.formType) && ids.TRANSCRIPT && ids.LEGACY) return true;
  return hasGeneratedScribeOutput(job.AiScribeContents);
}

/**
 * Which report a scribe-report tab is rendering for a given AI Scribe job.
 *
 * 'ptp'  — the PR report itself (PR-1 / PR-2 / PR-4), keyed off the job's
 *          formType.
 * others — a SIBLING form produced by a PR-1/PR-2 run: the DWC Form RFA, the
 *          Injection form, the Work Status form. Same scribe job and the same
 *          generated-document editor, but each owns its own
 *          `ai_scribe_contents` row, and its readiness is driven by its
 *          sub-report status rather than by a content row simply existing — a
 *          gated form can legitimately never be produced at all.
 */
export const SCRIBE_REPORT_VARIANTS = [
  'ptp',
  'rfa',
  'injection',
  'work-status',
] as const;

export type ScribeReportVariant = (typeof SCRIBE_REPORT_VARIANTS)[number];

/** Every variant except the PR report itself — i.e. the sibling forms. */
export type SiblingReportVariant = Exclude<ScribeReportVariant, 'ptp'>;

export interface SiblingReportConfig {
  documentType: GeneratedDocumentType;
  outputType: SiblingContentOutputType;
  reportLabel: string;
  documentName: string;
  /**
   * Whether this form runs behind a requirement gate.
   *
   * true  — it may legitimately never be produced, so 'NOT_REQUIRED' is a real
   *         state and the tab renders a "not required" panel for it.
   * false — it runs on every PR-1/PR-2 run, so 'NOT_REQUIRED' is unreachable.
   *         A run that produced nothing is a FAILURE, not a decline, and must
   *         read as one — showing "not required" there would tell a physician
   *         the form was never wanted when in fact it crashed.
   */
  gated: boolean;
  /**
   * The "not required" panel's copy — set for every GATED form, because what
   * a decline means is per form: RFA's gate reads whether the report
   * requested an authorization, Injection's reads whether this visit's
   * procedures include an injection. Absent for an ungated form, which never
   * renders that panel.
   */
  notRequiredPanel?: {
    title: string;
    message: string;
  };
}

/**
 * Single source of truth for every sibling form, keyed by variant.
 *
 * Replaces RFA_REPORT_CONFIG. Deliberately NOT keyed by form type: a sibling
 * document is the same document whether the requesting report was a PR-1 or a
 * PR-2, which is why this is a flat map while PTP_FORM_TYPE_CONFIG is not.
 *
 * Adding a fourth sibling form should be one entry here plus one registry entry
 * on the backend — nothing else in the tab machinery should need touching.
 */
export const SIBLING_REPORT_CONFIG: Record<
  SiblingReportVariant,
  SiblingReportConfig
> = {
  rfa: {
    documentType: 'RFA',
    outputType: 'RFA',
    reportLabel: 'RFA',
    documentName: 'rfa',
    gated: true,
    notRequiredPanel: {
      title: 'RFA Not Requested',
      message: 'This report did not request one, so no RFA form was generated.',
    },
  },
  injection: {
    documentType: 'INJECTION',
    outputType: 'INJECTION',
    reportLabel: 'Injection',
    documentName: 'injection',
    gated: true,
    notRequiredPanel: {
      title: 'No injection procedure this visit',
      message:
        'No injection procedure was documented for this visit, so no ' +
        'Injection form was generated.',
    },
  },
  'work-status': {
    documentType: 'WORK_STATUS',
    outputType: 'WORK_STATUS',
    reportLabel: 'Work Status',
    documentName: 'work-status',
    gated: false,
  },
};

/** The sibling variants, in the order their tabs should appear. */
export const SIBLING_REPORT_VARIANTS = Object.keys(
  SIBLING_REPORT_CONFIG,
) as SiblingReportVariant[];

/**
 * @deprecated Use SIBLING_REPORT_CONFIG.rfa. Kept so existing RFA call sites
 * compile through the transition.
 */
export const RFA_REPORT_CONFIG = SIBLING_REPORT_CONFIG.rfa;

/** Narrows a variant to a sibling form (i.e. anything but the PR report). */
export function isSiblingReportVariant(
  variant: ScribeReportVariant,
): variant is SiblingReportVariant {
  return variant !== 'ptp';
}

/**
 * Content outputType → the spelling the voice-edit route expects.
 *
 * A FOURTH spelling of the same thing, and the reason this map exists rather
 * than a toLowerCase() at the call site: PR-4 is 'PR4' as a content outputType,
 * 'PR4_REPORT' as a documentType, 'ptp_pr4_report' as a template use_case, and
 * 'pr4_report' in the report-kind registry that the voice-edit DTO validates
 * against. Lower-casing 'PR4' yields 'pr4', which that DTO rejects with a 400.
 */
export const VOICE_EDIT_OUTPUT_TYPE: Record<AiScribeContentOutputType, string> =
  {
    PR1: 'pr1',
    PR2: 'pr2',
    PR4: 'pr4_report',
    RFA: 'rfa',
    INJECTION: 'injection',
    WORK_STATUS: 'work_status',
    LEGACY: '',
    TRANSCRIPT: '',
    GENERAL: '',
  };

/**
 * What the user should know about a sibling form's FAILED run, keyed by the
 * error code the worker reports (its ApplicationFailure type, stored as the
 * sub-report's error_code). Only failures the USER can fix from the
 * dictation are listed — any other code keeps the generic failure text.
 */
export const SUB_REPORT_FAILURE_REASONS: Readonly<Record<string, string>> = {
  RfaNoTreatmentItemsError:
    'No requested treatment or service was found in the dictation — the ' +
    'RFA needs at least one.',
  InjectionNoProcedureError:
    'No injection procedure was found in the dictation.',
};

/** The user-facing reason a sibling form failed, or null when none is known. */
export function getSubReportFailureReason(
  job: Pick<AiScribeJob, 'subReportErrorCodes'> | null | undefined,
  outputType: AiScribeContentOutputType,
): string | null {
  const code = job?.subReportErrorCodes?.[outputType];
  return (code && SUB_REPORT_FAILURE_REASONS[code]) || null;
}

/**
 * generation_profile_targets.use_case per PR form type — the key the template
 * catalog (GET /generation-templates) and the stored generation_selections row
 * are both looked up by.
 *
 * Deliberately NOT the same strings as the report outputType ('PR1'/'PR2') or
 * the Temporal report kind ('pr1'/'pr2'): use_case names a TEMPLATE PACKAGE
 * family, and PR-4's already-shipped value is 'ptp_pr4_report'. The other two
 * follow that spelling so the three sort together and cannot be confused with
 * an outputType at a call site.
 *
 * A form type absent from this map has no template catalog, so its dropdown
 * stays hidden and the backend resolves the org default.
 */
export const GENERATION_USE_CASE_BY_FORM_TYPE: Record<string, string> = {
  'pr-1': 'ptp_pr1_report',
  'pr-2': 'ptp_pr2_report',
  'pr-4': 'ptp_pr4_report',
  // Standalone-form jobs: each its own package, independent of the PR sets.
  injection: 'ptp_injection_report',
  rfa: 'ptp_rfa_report',
  'work-status': 'ptp_work_status_report',
};

/**
 * AI Scribe "AI Generated / Dictation" radio. Defined in
 * generation-mode.constants.ts (shared with the QME Generate card) and
 * re-exported here so existing PTP imports keep working.
 */
export {
  DEFAULT_GENERATION_MODE,
  GENERATION_MODE_OPTIONS,
} from './generation-mode.constants';
export type { GenerationMode } from './generation-mode.constants';

/** One row of GET /generation-templates `templates[]`. */
export interface GenerationTemplateOption {
  id: string;
  displayName: string;
  description: string | null;
  scope: 'provider' | 'organization' | 'default';
}
